// game-picks-data.js - pure helpers for post-game nominations (top-3 / bottom-2).
// No DOM/network, so the tally maths is unit-tested. Feeds long-term tracking
// (a repeated top-3 player is a real best-of-the-best signal) and, later, the
// director-only bias view.

// Ranked top picks are worth more the higher they place.
export const RANK_POINTS = { 1: 3, 2: 2, 3: 1 };

/**
 * How much of "After the game" a team gets, by age.
 *
 *   'none'      U7 and U8. No picks at all: at that age the club does not rank
 *               children against each other in any direction. They get star
 *               certificates rather than levels for the same reason.
 *   'top-only'  U9. A coach can highlight up to three players who did well,
 *               but nothing negative is ever recorded against a nine year old.
 *   'full'      U10 and up. Top three plus an optional bottom two.
 *
 * Reads the age off the team id, never off age_group: every team from U7 to U12
 * carries the same "U11-U12" age_group text, so age_group cannot tell a U7 side
 * from a U12 one.
 */
export function gamePicksMode(teamId) {
  const m = /^U(\d+)/i.exec(String(teamId || ''));
  if (!m) return 'full';           // ZZTEST and anything unparseable behave as before
  const age = parseInt(m[1], 10);
  if (age <= 8) return 'none';
  if (age === 9) return 'top-only';
  return 'full';
}

/** Can this team record post-game picks at all? */
export function hasGamePicks(teamId) {
  return gamePicksMode(teamId) !== 'none';
}

/**
 * Tally nominations per player. Input rows: { player_id, kind:'top'|'bottom',
 * rank }. Returns Map player_id -> { top, top1, points, bottom } where:
 *   top    = number of top-3 nominations
 *   top1   = number of #1 nominations
 *   points = weighted (3/2/1 by rank) - the best-of-best signal
 *   bottom = number of bottom nominations
 */
export function nominationTally(rows) {
  const out = new Map();
  (rows || []).forEach(r => {
    const acc = out.get(r.player_id) || { top: 0, top1: 0, points: 0, bottom: 0 };
    if (r.kind === 'top') {
      acc.top += 1;
      if (r.rank === 1) acc.top1 += 1;
      acc.points += RANK_POINTS[r.rank] || 0;
    } else if (r.kind === 'bottom') {
      acc.bottom += 1;
    }
    out.set(r.player_id, acc);
  });
  return out;
}

/**
 * How many distinct games a set of nomination rows covers (author + date),
 * so tallies can be shown as "top-3 in N of M games".
 */
export function gameCount(rows) {
  const games = new Set();
  (rows || []).forEach(r => games.add(`${r.author_code || ''}|${r.game_date}`));
  return games.size;
}

/**
 * Validate a coach's in-progress picks before saving.
 * top = ordered array of up to 3 player ids (index 0 = #1); bottom = up to 2.
 * Rules: 1 to 3 top, at most 2 bottom, no player in both, no duplicates.
 */
export function validatePicks(top, bottom) {
  const t = (top || []).filter(Boolean);
  const b = (bottom || []).filter(Boolean);
  if (!t.length) return { ok: false, reason: 'need_top' };
  if (t.length > 3) return { ok: false, reason: 'too_many_top' };
  if (b.length > 2) return { ok: false, reason: 'too_many_bottom' };
  if (new Set(t).size !== t.length) return { ok: false, reason: 'dup_top' };
  if (new Set(b).size !== b.length) return { ok: false, reason: 'dup_bottom' };
  if (t.some(id => b.includes(id))) return { ok: false, reason: 'overlap' };
  return { ok: true };
}

/**
 * Build the rows to persist from ordered top + bottom id arrays, stamped with
 * the game/author context.
 */
export function buildRows(top, bottom, ctx) {
  const rows = [];
  (top || []).forEach((id, i) => {
    if (id) rows.push({ ...ctx, player_id: id, kind: 'top', rank: i + 1 });
  });
  (bottom || []).forEach(id => {
    if (id) rows.push({ ...ctx, player_id: id, kind: 'bottom', rank: null });
  });
  return rows;
}
